import React, {useCallback, useEffect, useState} from "react"
import {AddCircleOutline, ArticleOutlined, ArrowBack} from "@mui/icons-material"
import {apiV2} from "../../../api/axiosConfig";
import {useNavigate} from "react-router-dom";
import ModalV2 from "../../../components/portal/ModalV2";
import {DeliveryMethod, MassWarningTemplate} from "../../siren/typedefs";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {useGetMassWarningTemplates} from "../../siren/hooks/useGetMassWarningTemplates";

type Step = "choose" | "select-template"

type MassWarningModalProps = {
    show: boolean,
    setShow: (show: boolean) => void,
    clientID?: string | undefined,
    // Provide either params (fetches userIDs from world-search API) or preloadedUserIDs directly
    params?: Record<string, string>,
    preloadedUserIDs?: string[],
}

function formatMethod(method: string): string {
    return method
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace(/\bSms\b/g, "SMS");
}

function DeliveryMethodPills({methods}: {methods: DeliveryMethod[]}) {
    if (methods.length === 0) return <span className="text-grey text-sm">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {methods.map(m => (
                <span key={m} className="px-2 py-0.5 rounded-full bg-light-light-grey text-xs text-dark-dark-grey whitespace-nowrap">
                    {formatMethod(m)}
                </span>
            ))}
        </div>
    );
}

export function MassWarningModal(props: MassWarningModalProps) {
    const [userIDs, setUserIDs] = useState<string[]>(props.preloadedUserIDs ?? []);
    const [loading, setLoading] = useState<boolean>(!props.preloadedUserIDs);
    const [step, setStep] = useState<Step>("choose");
    const [selectedTemplateID, setSelectedTemplateID] = useState<string[]>([]);
    const navigate = useNavigate();

    const getTemplates = useGetMassWarningTemplates("alert", props.clientID ?? null);

    useEffect(() => {
        if (!props.show) return;
        setStep("choose");
        setSelectedTemplateID([]);

        if (props.preloadedUserIDs) {
            setUserIDs(props.preloadedUserIDs);
            setLoading(false);
            return;
        }

        if (!props.params) return;
        setLoading(true);
        apiV2.get<string[]>("/world-search/users", {params: props.params}).then(res => {
            setUserIDs(res.data);
            setLoading(false);
        });
    }, [props.params, props.preloadedUserIDs, props.show]);

    const navigateToCreate = useCallback((templateID: string) => {
        const uuid = crypto.randomUUID();
        localStorage.setItem(`mass-warning-users-${uuid}`, JSON.stringify(userIDs));
        navigate(`/siren/alerts?subPage=create&clientID=${props.clientID}&userList=${uuid}&templateID=${templateID}`);
    }, [userIDs, props.clientID, navigate]);

    const handleUseTemplate = useCallback(() => {
        if (selectedTemplateID.length === 0) return;
        navigateToCreate(selectedTemplateID[0]);
    }, [selectedTemplateID, navigateToCreate]);

    if (!props.clientID) return null;

    const isChoose = step === "choose";

    return (
        <ModalV2 open={props.show} setOpen={props.setShow} contentClassName={isChoose ? "w-[420px]" : "w-[700px]"}>
            <div className="flex items-center gap-3 mb-2">
                {step === "select-template" && (
                    <button onClick={() => setStep("choose")} className="text-grey hover:text-dark-dark-grey transition-colors">
                        <ArrowBack style={{fontSize: 20}}/>
                    </button>
                )}
                <h2 className="text-lg font-bold">Send Siren Alert</h2>
            </div>

            {loading && <p className="text-sm text-light-grey">Loading...</p>}

            {!loading && step === "choose" && (
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-dark-grey">
                        Sending to {userIDs.length} {userIDs.length === 1 ? "user" : "users"}.
                        How would you like to create this alert?
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigateToCreate("no_template")}
                            className="flex-1 flex flex-col items-center gap-2 p-5 rounded-lg border-2 border-light-grey hover:border-blue hover:bg-white-blue transition-colors group"
                        >
                            <AddCircleOutline className="text-grey group-hover:text-blue transition-colors" style={{fontSize: 28}}/>
                            <span className="font-semibold text-sm text-dark-dark-grey">Create New</span>
                            <span className="text-xs text-grey text-center">Start from scratch</span>
                        </button>
                        <button
                            onClick={() => setStep("select-template")}
                            className="flex-1 flex flex-col items-center gap-2 p-5 rounded-lg border-2 border-light-grey hover:border-blue hover:bg-white-blue transition-colors group"
                        >
                            <ArticleOutlined className="text-grey group-hover:text-blue transition-colors" style={{fontSize: 28}}/>
                            <span className="font-semibold text-sm text-dark-dark-grey">Use Template</span>
                            <span className="text-xs text-grey text-center">Pre-fill from a template</span>
                        </button>
                    </div>
                </div>
            )}

            {!loading && step === "select-template" && (
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-dark-grey">
                        Sending to {userIDs.length} {userIDs.length === 1 ? "user" : "users"}.
                    </p>
                    <PaginatedSelector<MassWarningTemplate>
                        headers={["Name", "Type", "Delivery Methods"]}
                        columnTemplate="minmax(0,1fr) 90px minmax(0,1.5fr)"
                        getItems={getTemplates}
                        getColumns={(t) => [
                            t.name,
                            t.type ? t.type.charAt(0).toUpperCase() + t.type.slice(1) : "—",
                            t.useDefaultDeliveryMethods
                                ? <span className="text-xs bg-global-purple text-white px-1.5 py-0.5 rounded">Default</span>
                                : <DeliveryMethodPills methods={t.deliveryMethods ?? []}/>,
                        ]}
                        selectedIDs={selectedTemplateID}
                        setSelectedIDs={(ids) => {
                            const latest = ids.filter(id => !selectedTemplateID.includes(id));
                            setSelectedTemplateID(latest.length ? [latest[latest.length - 1]] : ids.slice(-1));
                        }}
                        disableSelect={false}
                        hideSelectAll={true}
                        nonClickableColumns={[2]}
                    />
                    <div className="flex justify-end pt-1">
                        <ButtonV2
                            label="Use Template"
                            onClick={handleUseTemplate}
                            disabled={selectedTemplateID.length === 0}
                        />
                    </div>
                </div>
            )}
        </ModalV2>
    );
}
